import React, { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useDispatch, useSelector } from 'react-redux'

import { Grid, Paper, Table, TableBody, TableCell, TableContainer, TableHead, TableRow } from '@mui/material'

import { Context } from 'components/core/Acquiring/AcquiringOverview/components/AcquiringTransfersContext'
import AcquiringTransfersDownload from 'components/core/Acquiring/AcquiringOverview/components/AcquiringTransfersDownload'
import { parseAcquiringDefaultValues } from 'components/core/Acquiring/AcquiringState'
import { ErrorAlert } from 'components/core/Alert'
import { TableFrame, TableSplitView } from 'components/core/Tables'
import {
    finishGetAccountsAcquiring,
    finishGetDefaultCardAcquiring,
    startGetAccountsAcquiring,
    startGetDefaultCardAcquiring,
} from 'redux/acquiring/acquiring-actions'
import { RootState } from 'redux/store'

import { COMPONENT_EXTENDER } from 'extender'

const SkeletonSection = COMPONENT_EXTENDER('components/core/Skeleton/SkeletonSection/SkeletonSection-component')

const AcquiringOverview: React.FC<AcquiringApiProps> = () => {
    const dispatch = useDispatch()
    const { t } = useTranslation()

    const { defaultCardAcquiring, accountsAcquiring, loader } = useSelector((state: RootState) => state.acquiring)

    useEffect(() => {
        dispatch(startGetDefaultCardAcquiring())
        dispatch(startGetAccountsAcquiring())

        return () => {
            dispatch(finishGetDefaultCardAcquiring())
            dispatch(finishGetAccountsAcquiring())
        }
    }, [])

    const parsedCardData: Array<DefaultCardValue> = defaultCardAcquiring && parseAcquiringDefaultValues(defaultCardAcquiring)

    const [transfersInputs, setTransfersInputs] = useState<TransfersInputs>({ from: null, to: null })

    const contextValues = { transfersInputs, setTransfersInputs, loader }

    return (
        <div style={{ marginTop: 20 }}>
            <ErrorAlert />
            {loader?.GET_DEFAULT_CARD_ACQUIRING ? (
                <SkeletonSection />
            ) : (
                <>
                    <h3>{t('Acquiring.Overview')}</h3>
                    {!defaultCardAcquiring?.isEnabled ? (
                        <p>Your business conditions are not set yet.</p>
                    ) : (
                        <TableFrame>
                            <TableSplitView height="70" data={parsedCardData} />
                        </TableFrame>
                    )}
                </>
            )}
            {loader?.GET_ACCOUNTS_ACQUIRING ? (
                <SkeletonSection />
            ) : (
                <>
                    <h3>{t('Acquiring.Accounts')}</h3>

                    <Grid container spacing={0}>
                        <Grid item md={12} lg={12} className="verticalScrollable">
                            <TableContainer component={Paper} style={{ border: '1px solid #E3E3E3' }}>
                                <Table aria-label="collapsible table">
                                    <TableHead>
                                        <TableRow>
                                            <TableCell align="left" padding="normal">
                                                {t('Acquiring.Currency')}
                                            </TableCell>
                                            <TableCell align="left">{t('Acquiring.RollingReserveHeld')}</TableCell>
                                            <TableCell align="left" padding="normal">
                                                {t('Acquiring.BalanceHeld')}
                                            </TableCell>
                                            <TableCell align="left" padding="normal">
                                                {t('Acquiring.AvailableBalance')}
                                            </TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {!accountsAcquiring?.length ? (
                                            <TableRow>
                                                <TableCell>{t('Acquiring.NoResults')}</TableCell>
                                            </TableRow>
                                        ) : (
                                            accountsAcquiring &&
                                            accountsAcquiring?.map((account: any, i: any) => (
                                                <TableRow key={i}>
                                                    <TableCell>{account.currencyCode}</TableCell>
                                                    <TableCell>{account.heldRollingReserve}</TableCell>
                                                    <TableCell>{account.heldBalance}</TableCell>
                                                    <TableCell>{account.availableBalance}</TableCell>
                                                </TableRow>
                                            ))
                                        )}
                                    </TableBody>
                                </Table>
                            </TableContainer>
                        </Grid>
                    </Grid>
                    <Context.Provider value={contextValues}>
                        <h3>{t('Acquiring.Reporting')}</h3>
                        <AcquiringTransfersDownload />
                    </Context.Provider>
                </>
            )}
        </div>
    )
}

export default AcquiringOverview
